import React from 'react';
import { ExternalLink, AlertCircle, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KeySelect from './KeySelect';
import { kindAllowsKey } from '../lib/classifyLink';

// Song links split by key (R-SONGROW 2, D24, L13-4): a link is "for this key"
// when link.key === key or link.key is null; the rest are "other keys". Used
// by the admin song drawer and the member song card; changing the key there
// only re-splits the list (local, never written).

const CHART_KINDS = ['pdf', 'drive', 'dropbox', 'other'];

// Tints by kind (Section 11.5): youtube red, spotify green, pdf amber, drive/dropbox sky.
const KIND_STYLE = {
  youtube: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  spotify: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  apple: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
  pdf: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  drive: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  dropbox: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  audio: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
  other: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
};
const KIND_BADGE = {
  youtube: 'YouTube', spotify: 'Spotify', apple: 'Apple', pdf: 'PDF',
  drive: 'Drive', dropbox: 'Dropbox', audio: 'Audio', other: 'Link',
};

export function splitLinks(links, key) {
  const forKey = [];
  const other = [];
  (links || []).forEach((l) => {
    if (l.key === null || l.key === undefined || l.key === key) forKey.push(l);
    else other.push(l);
  });
  const hasChart = !!key && forKey.some((l) => CHART_KINDS.includes(l.kind) && l.key === key);
  return { forKey, other, hasChart };
}

export function youtubeSearchUrl(title, artist) {
  const q = [title, artist].filter(Boolean).join(' ');
  return `https://www.youtube.com/results?${new URLSearchParams({ search_query: q }).toString()}`;
}

function sublabel(link) {
  if (link.kind === 'drive') return 'Opens in Google Drive';
  if (link.kind === 'dropbox') return 'Opens in Dropbox';
  try { return new URL(link.url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function LinkRow({ link, editable, onDelete, onSetKey, busy }) {
  return (
    <div data-testid={`song-link-${link.id}`}>
      <div className="flex items-center gap-2 bg-slate-50 dark:bg-[#34373c] rounded-lg px-3 py-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${KIND_STYLE[link.kind] || KIND_STYLE.other}`}>
          {KIND_BADGE[link.kind] || 'Link'}
        </span>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 flex-1 group"
          data-testid={`song-link-open-${link.id}`}
        >
          <span className="flex items-center gap-1 text-sm font-medium text-slate-800 dark:text-slate-100">
            <span className="truncate group-hover:underline underline-offset-2">{link.label}</span>
            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
          </span>
          <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">{sublabel(link)}</span>
        </a>
        {editable && kindAllowsKey(link.kind) ? (
          <div className="flex items-center gap-1">
            <KeySelect value={link.key} onChange={(k) => onSetKey(link, k)} disabled={busy} className="w-[70px]" data-testid={`song-link-key-${link.id}`} />
            {link.key && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => onSetKey(link, null)} disabled={busy} aria-label="Any key" data-testid={`song-link-key-clear-${link.id}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ) : link.key ? (
          <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">{link.key}</span>
        ) : null}
        {editable && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => onDelete(link)} disabled={busy} aria-label="Remove link" data-testid={`song-link-delete-${link.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      {link.own_storage_warning && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1" data-testid={`song-link-storage-warning-${link.id}`}>
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> This file is in Worship OS post storage and may be removed after 90 days.
        </p>
      )}
    </div>
  );
}

const HEAD = 'text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium';

export default function SongLinks({ links, keyValue, searchUrl, editable = false, onDelete, onSetKey, busy = false }) {
  const { forKey, other, hasChart } = splitLinks(links, keyValue);
  if (!forKey.length && !other.length) {
    return (
      <div className="rounded-lg bg-slate-50 dark:bg-[#34373c] px-3 py-3 text-center" data-testid="song-links-empty">
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">No links yet.</p>
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-teal-700 dark:text-teal-300 underline underline-offset-2"
          data-testid="song-links-youtube-search"
        >
          Search YouTube <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="song-links">
      <div>
        <p className={`${HEAD} mb-2`} data-testid="song-links-for-key-heading">{keyValue ? `FOR THIS KEY · ${keyValue}` : 'ANY KEY'}</p>
        {forKey.length ? (
          <div className="space-y-2" data-testid="song-links-for-key">
            {forKey.map((l) => <LinkRow key={l.id} link={l} editable={editable} onDelete={onDelete} onSetKey={onSetKey} busy={busy} />)}
          </div>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">No links for this key.</p>
        )}
      </div>
      {keyValue && !hasChart && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="song-links-no-chart">No chart in {keyValue} yet</p>
      )}
      {other.length > 0 && (
        <div>
          <p className={`${HEAD} mb-2`}>OTHER KEYS</p>
          <div className="space-y-2" data-testid="song-links-other">
            {other.map((l) => <LinkRow key={l.id} link={l} editable={editable} onDelete={onDelete} onSetKey={onSetKey} busy={busy} />)}
          </div>
        </div>
      )}
    </div>
  );
}
